// ╔══════════════════════════════════════════════════════════════╗
// ║  ExitModal — records a documented exit when status → terminated║
// ╚══════════════════════════════════════════════════════════════╝

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getErrorMessage } from '../lib/api.js';
import { useToast } from './Toast.jsx';
import { Btn, Field, Input, Modal, Select } from './ui.jsx';

const REASONS = [
  { value: 'resignation', label: 'استقالة' },
  { value: 'termination', label: 'إنهاء خدمة' },
  { value: 'retirement', label: 'تقاعد' },
  { value: 'death', label: 'وفاة' },
  { value: 'absconded', label: 'ترك العمل' },
  { value: 'other', label: 'آخر' },
];

export default function ExitModal({ employee, onClose, onSuccess }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [form, setForm] = useState({
    reason: 'resignation',
    exitDate: new Date().toISOString().slice(0, 10),
    interviewNotes: '',
    finalSettlement: '',
  });

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const mut = useMutation({
    mutationFn: (payload) => api.post('/exits', { ...payload, employeeId: employee.id }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employees'] });
      qc.invalidateQueries({ queryKey: ['employee', employee.id] });
      qc.invalidateQueries({ queryKey: ['exits'] });
      qc.invalidateQueries({ queryKey: ['reports', 'dashboard'] });
      toast.push({ tone: 'success', title: 'تم توثيق خروج الموظف' });
      onSuccess?.();
    },
    onError: (err) =>
      toast.push({ tone: 'danger', title: 'فشل التوثيق', body: getErrorMessage(err) }),
  });

  function handleSubmit(e) {
    e.preventDefault();
    const payload = {
      reason: form.reason,
      exitDate: form.exitDate,
    };
    if (form.interviewNotes.trim()) payload.interviewNotes = form.interviewNotes.trim();
    if (form.finalSettlement) payload.finalSettlement = Number(form.finalSettlement);
    mut.mutate(payload);
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`توثيق خروج: ${employee.name}`}
      size="md"
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>إلغاء</Btn>
          <Btn
            type="submit"
            form="exit-form"
            variant="danger"
            icon="check"
            loading={mut.isPending}
          >
            تأكيد الخروج
          </Btn>
        </>
      }
    >
      <div className="mb-4 px-3 py-2.5 rounded-xl bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 text-sm">
        ⚠️ هذا الإجراء سيُحوّل حالة الموظف إلى <strong>"منتهية الخدمة"</strong> ويحفظ سجلاً دائماً للسبب.
      </div>
      <form id="exit-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="سبب الخروج">
            <Select value={form.reason} onChange={upd('reason')} required>
              {REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="تاريخ الخروج">
            <Input type="date" value={form.exitDate} onChange={upd('exitDate')} required />
          </Field>
        </div>
        <Field label="المخالصة النهائية (جنيه — اختياري)">
          <Input
            type="number"
            value={form.finalSettlement}
            onChange={upd('finalSettlement')}
            placeholder="مثلاً: 12000"
            min={0}
          />
        </Field>
        <Field label="ملاحظات / محضر الخروج" hint="موصى به للسجل القانوني">
          <textarea
            value={form.interviewNotes}
            onChange={upd('interviewNotes')}
            rows={4}
            placeholder="تفاصيل إضافية عن سبب الخروج، توصيات، إلخ..."
            className="w-full px-4 py-3 rounded-xl bg-ink-50 dark:bg-ink-800 ring-1 ring-ink-200 dark:ring-ink-700 focus:ring-2 focus:ring-brand-400 outline-none text-sm"
          />
        </Field>
      </form>
    </Modal>
  );
}
